import React, { useState } from "react";
import SubHeader from "../../components/SubHeader/SubHeader";
import InputField from "../../components/InputField/InputField";
import Loader from "../../components/Loader/Loader";
import DummyUser from "../../assets/SampleUser.jpg";
import { postApi } from "../../utils/apiService";
import { CHANGE_PASSWORD } from "../../utils/apiPath";
import { errorToast, successToast } from "../../services/ToastHelper";
import { Table, Tag } from "antd";
import "./Settings.scss";

const Settings = () => {
  const storedUser = sessionStorage.getItem("user");
  const userData = storedUser ? JSON.parse(storedUser) : {};
  const role = sessionStorage.getItem("role") || "";
  const branchName = (() => {
    try {
      const branches = JSON.parse(sessionStorage.getItem("branches") || "[]");
      if (Array.isArray(branches) && branches.length > 0) {
        return branches.map((b) => b.branchName).join(", ");
      }
    } catch {
      // fall back to stored branchName below
    }
    return sessionStorage.getItem("branchName") || "";
  })();
  
  const gradeAccessStr = sessionStorage.getItem("gradeAccess");
  const gradeAccess = gradeAccessStr ? JSON.parse(gradeAccessStr) : [];
  
  const permissionsStr = sessionStorage.getItem("permissions");
  const permissions = permissionsStr ? JSON.parse(permissionsStr) : [];

  const permissionColumns = [
    { title: "Module", dataIndex: "moduleName", key: "moduleName" },
    { title: "View", dataIndex: "canView", key: "canView", render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag> },
    { title: "Add", dataIndex: "canAdd", key: "canAdd", render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag> },
    { title: "Edit", dataIndex: "canEdit", key: "canEdit", render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag> },
    { title: "Delete", dataIndex: "canDelete", key: "canDelete", render: (val) => val ? <Tag color="green">Yes</Tag> : <Tag color="red">No</Tag> },
  ];

  const [isLoading, setIsLoading] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setPasswordForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleChangePassword = async () => {
    const newErrors = {};
    if (!passwordForm.currentPassword) newErrors.currentPassword = "Required";
    if (!passwordForm.newPassword) newErrors.newPassword = "Required";
    if (!passwordForm.confirmPassword) newErrors.confirmPassword = "Required";
    
    if (passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        currentPassword: passwordForm.currentPassword,
        password: passwordForm.newPassword,
      };

      const { statusCode, message } = await postApi(CHANGE_PASSWORD, payload);
      
      if (statusCode === 200) {
        successToast("Password changed successfully");
        setPasswordForm({
          currentPassword: "",
          newPassword: "",
          confirmPassword: "",
        });
      } else {
        errorToast(message);
      }
    } catch (err) {
      errorToast("Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="settings-page">
      {isLoading && <Loader />}
      
      <SubHeader title="Settings" showBack={false} />

      <div className="settings-section">
        <h2>Profile Details</h2>
        <div className="profile-avatar-container">
          <img src={userData.profilePicture || DummyUser} alt="Profile" />
        </div>
        
        <div className="profile-grid">
          <InputField title="User Name" value={userData.userName || "-"} disabled />
          <InputField title="Email ID" value={userData.emailId || "-"} disabled />
          <InputField title="Phone Number" value={userData.phoneNumber || "-"} disabled />
          <InputField title="Role" value={role || "-"} disabled />
          <InputField title="Branch" value={branchName || "-"} disabled />
        </div>
      </div>

      {role === "Staff" ? (
        <div className="settings-section">
          <h2>Permissions & Access</h2>
          
          <div style={{ marginBottom: 24 }}>
            <h3 style={{ fontSize: '1rem', marginBottom: 12, color: '#475569' }}>Grade Access</h3>
            {gradeAccess.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {gradeAccess.map(g => (
                  <Tag key={g.gradeId} color="blue" style={{ padding: '4px 12px', fontSize: '14px' }}>
                    {g.gradeName}
                  </Tag>
                ))}
              </div>
            ) : (
              <p style={{ color: '#94a3b8', margin: 0 }}>No grade access assigned.</p>
            )}
          </div>

          <div>
            <h3 style={{ fontSize: '1rem', marginBottom: 12, color: '#475569' }}>Module Permissions</h3>
            <Table 
              columns={permissionColumns} 
              dataSource={permissions} 
              rowKey={(record) => record.sectionId || record.moduleName} 
              pagination={false}
              bordered
              size="small"
            />
          </div>
        </div>
      ) : (
        <div className="settings-section">
          <h2>Permissions & Access</h2>
          <p style={{ color: '#475569', margin: 0 }}>
            As a <strong>{role}</strong>, you have full administrator access to all modules and grades.
          </p>
        </div>
      )}

      <div className="settings-section">
        <h2>Change Password</h2>
        <div className="change-password-form">
          <InputField
            title="Current Password"
            type="password"
            placeholder="Enter current password"
            value={passwordForm.currentPassword}
            onChange={(e) => handleInputChange("currentPassword", e.target.value)}
            errorText={errors.currentPassword}
            required
          />
          <InputField
            title="New Password"
            type="password"
            placeholder="Enter new password"
            value={passwordForm.newPassword}
            onChange={(e) => handleInputChange("newPassword", e.target.value)}
            errorText={errors.newPassword}
            required
          />
          <InputField
            title="Confirm Password"
            type="password"
            placeholder="Confirm new password"
            value={passwordForm.confirmPassword}
            onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
            errorText={errors.confirmPassword}
            required
          />
          <div className="form-actions">
            <button onClick={handleChangePassword}>Update Password</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
